import {
    Upload as UploadIcon,
    Edit as EditIcon,
    Gavel as AgreementIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import { Box, Grid, Card, CardContent, Typography, Chip, Alert, Divider } from '@mui/material';
import type { TFunction } from 'i18next';
import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import api from 'api';
import { DocumentStatus } from 'constant';
import { areAllUnsignedRetainersSigningBlocked } from 'helpers/applicationRetainers';
import { useApplicationRetainersLists } from 'hooks/useApplicationRetainers';
import { RootState } from 'redux/store';
import type { ApplicationDocumentInterface, TicketInterface } from 'types';

interface PendingActionApplication {
    id: number;
    program_name?: string | null;
    candidate_name?: string | null;
    date_agreement_signed?: string | Date | null;
}

interface PendingAction {
    applicationId: number;
    applicationName: string;
    id: string;
    type: 'document' | 'agreement' | 'ticket';
    title: string;
    description: string;
    icon: React.ReactNode;
    severity: 'error' | 'warning' | 'info';
    path: string;
    count?: number;
}

// Custom hook to handle all applications with proper React hooks rules
const usePendingActionsForAllApplications = (
    applications: PendingActionApplication[],
    t: TFunction
) => {
    const [documentsMap, setDocumentsMap] = useState<
        Record<number, ApplicationDocumentInterface[]>
    >({});
    const [ticketsMap, setTicketsMap] = useState<Record<number, TicketInterface[]>>({});
    const { data: retainersMap } = useApplicationRetainersLists(
        applications.map((application) => application.id)
    );

    useEffect(() => {
        const fetchAllDocuments = async () => {
            const map: Record<number, ApplicationDocumentInterface[]> = {};
            await Promise.all(
                applications.map(async (app) => {
                    if (!app) return;
                    try {
                        const response = await api.application.documents.get(String(app.id));
                        map[app.id] = response.data as unknown as ApplicationDocumentInterface[];
                    } catch {
                        map[app.id] = [];
                    }
                })
            );
            setDocumentsMap(map);
        };

        const fetchAllTickets = async () => {
            const map: Record<number, TicketInterface[]> = {};
            await Promise.all(
                applications.map(async (app) => {
                    if (!app) return;
                    try {
                        const response = await api.tickets.getTicketsByApplication(String(app.id));
                        map[app.id] = response.data as unknown as TicketInterface[];
                    } catch {
                        map[app.id] = [];
                    }
                })
            );
            setTicketsMap(map);
        };

        if (applications.length > 0) {
            fetchAllDocuments();
            fetchAllTickets();
        }
    }, [applications]);

    return useMemo(() => {
        const actions: PendingAction[] = [];

        applications.forEach((application) => {
            if (!application) return;

            const documents = documentsMap[application.id] || [];
            const ticketList = ticketsMap[application.id] || [];
            const applicationRetainers = retainersMap?.[application.id]?.retainers ?? [];
            const signingUnavailable = areAllUnsignedRetainersSigningBlocked(applicationRetainers);

            const basePath = `/visa-process/${application.id}`;
            const appName =
                application.program_name ||
                application.candidate_name ||
                `Application #${application.id}`;

            // Check for missing documents
            const missingDocs = documents.filter(
                (doc: ApplicationDocumentInterface) => doc.status === DocumentStatus.MISSING
            );
            if (missingDocs.length > 0) {
                actions.push({
                    applicationId: application.id,
                    applicationName: appName,
                    id: `${application.id}-missing-documents`,
                    type: 'document',
                    title: t('uploadDocumentsTitle'),
                    description:
                        t('dashboardActionsMissingDocuments', { count: missingDocs.length }) ||
                        `${missingDocs.length} document(s) need to be uploaded`,
                    icon: <UploadIcon />,
                    severity: 'error',
                    path: `${basePath}/documents`,
                    count: missingDocs.length,
                });
            }

            // Check for documents needing fixes or signatures
            const docsNeedingAction = documents.filter(
                (doc: ApplicationDocumentInterface) =>
                    doc.status === DocumentStatus.FIX_REQUIRED ||
                    doc.status === DocumentStatus.SIGNATURE_REQUIRED ||
                    doc.status === DocumentStatus.WAITING_FOR_APPROVAL
            );

            if (docsNeedingAction.length > 0) {
                actions.push({
                    applicationId: application.id,
                    applicationName: appName,
                    id: `${application.id}-fix-documents`,
                    type: 'document',
                    title: t('dashboardActionsFixDocuments') || 'Fix/Sign Documents',
                    description:
                        t('dashboardActionsDocumentsNeedAction', {
                            count: docsNeedingAction.length,
                        }) || `${docsNeedingAction.length} document(s) need your attention`,
                    icon: <EditIcon />,
                    severity: 'warning',
                    path: `${basePath}/documents`,
                    count: docsNeedingAction.length,
                });
            }

            // Check for unsigned agreement
            if (!application.date_agreement_signed && !signingUnavailable) {
                actions.push({
                    applicationId: application.id,
                    applicationName: appName,
                    id: `${application.id}-agreement`,
                    type: 'agreement',
                    title: t('signAgreement'),
                    description:
                        t('dashboardActionsAgreementPending') ||
                        'Consultation agreement requires your signature',
                    icon: <AgreementIcon />,
                    severity: 'warning',
                    path: `${basePath}/agreement`,
                });
            }

            // Check for open tickets
            const openTickets = ticketList.filter(
                (ticket: TicketInterface) =>
                    ticket.status !== 'RESOLVED' && ticket.status !== 'CLOSED'
            );

            if (openTickets.length > 0) {
                actions.push({
                    applicationId: application.id,
                    applicationName: appName,
                    id: `${application.id}-open-tickets`,
                    type: 'ticket',
                    title: t('dashboardActionsResolveTickets') || 'Resolve Issues',
                    description:
                        t('dashboardActionsOpenTickets', { count: openTickets.length }) ||
                        `${openTickets.length} issue(s) require resolution`,
                    icon: <WarningIcon />,
                    severity: 'error',
                    path: `${basePath}/documents`,
                    count: openTickets.length,
                });
            }
        });

        return actions;
    }, [applications, documentsMap, retainersMap, ticketsMap, t]);
};

const ApplicationActions = () => {
    const { t } = useTranslation();

    // Fetch all applications
    const { applications } = useSelector((state: RootState) => state.applications);

    // Get all pending actions for all applications
    const pendingActions = usePendingActionsForAllApplications(applications, t);

    if (applications.length === 0 || pendingActions.length === 0) {
        return null;
    }

    // Group actions by application
    const actionsByApplication = pendingActions.reduce(
        (acc, action) => {
            if (!acc[action.applicationId]) {
                acc[action.applicationId] = {
                    applicationName: action.applicationName,
                    actions: [],
                };
            }
            acc[action.applicationId].actions.push(action);
            return acc;
        },
        {} as Record<
            number,
            {
                applicationName: string;
                actions: PendingAction[];
            }
        >
    );

    const actionsByAppKeys = Object.keys(actionsByApplication);

    return (
        <Box sx={{ mb: 3 }}>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5, fontSize: '0.875rem' }}
            >
                {t('dashboardActionsImmediateActions') || 'Immediate Actions Required'}
            </Typography>

            {pendingActions.length > 0 && (
                <Alert
                    severity="info"
                    sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                    <Typography variant="body2">
                        {t('dashboardActionsSummary', { count: pendingActions.length }) ||
                            `You have ${pendingActions.length} pending item(s) that need attention.`}
                    </Typography>
                </Alert>
            )}

            {actionsByAppKeys.map((appId, appIndex) => {
                const { applicationName, actions } = actionsByApplication[Number(appId)];
                return (
                    <Box key={appId} sx={{ mb: 3 }}>
                        {actionsByAppKeys.length > 1 && (
                            <Typography
                                variant="subtitle2"
                                sx={{ mb: 1.5, fontSize: '0.875rem', fontWeight: 600 }}
                            >
                                {applicationName}
                            </Typography>
                        )}

                        <Grid container spacing={2}>
                            {actions.map((action) => (
                                <Grid size={{ xs: 12, sm: 6 }} key={action.id}>
                                    <Box
                                        component={Link}
                                        to={action.path}
                                        sx={{ textDecoration: 'none' }}
                                    >
                                        <Card
                                            sx={{
                                                height: '100%',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                border: '2px solid',
                                                borderColor:
                                                    action.severity === 'error'
                                                        ? 'error.main'
                                                        : action.severity === 'warning'
                                                          ? 'warning.main'
                                                          : 'info.main',
                                                backgroundImage:
                                                    action.severity === 'error'
                                                        ? 'linear-gradient(135deg, rgba(211, 47, 47, 0.04) 0%, rgba(211, 47, 47, 0.02) 100%)'
                                                        : action.severity === 'warning'
                                                          ? 'linear-gradient(135deg, rgba(245, 127, 23, 0.04) 0%, rgba(245, 127, 23, 0.02) 100%)'
                                                          : 'linear-gradient(135deg, rgba(13, 110, 253, 0.04) 0%, rgba(13, 110, 253, 0.02) 100%)',
                                                boxShadow: 'none',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: action.severity === 'error' ? 2 : 1,
                                                    borderColor:
                                                        action.severity === 'error'
                                                            ? 'error.dark'
                                                            : action.severity === 'warning'
                                                              ? 'warning.dark'
                                                              : 'info.dark',
                                                },
                                            }}
                                        >
                                            <CardContent
                                                sx={{
                                                    ':last-child': { pb: { xs: 1.5, xl: 2.5 } },
                                                    px: { xs: 1.75, xl: 2.5 },
                                                    py: 1.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: 1.5,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: 1.5,
                                                            bgcolor:
                                                                action.severity === 'error'
                                                                    ? 'rgba(211, 47, 47, 0.12)'
                                                                    : action.severity === 'warning'
                                                                      ? 'rgba(245, 127, 23, 0.12)'
                                                                      : 'rgba(13, 110, 253, 0.12)',
                                                            color:
                                                                action.severity === 'error'
                                                                    ? 'error.main'
                                                                    : action.severity === 'warning'
                                                                      ? 'warning.main'
                                                                      : 'info.main',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {action.icon}
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontSize: '0.938rem',
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {action.title}
                                                            </Typography>
                                                            {action.count && (
                                                                <Chip
                                                                    label={action.count}
                                                                    size="small"
                                                                    color={
                                                                        action.severity === 'error'
                                                                            ? 'error'
                                                                            : 'warning'
                                                                    }
                                                                    variant="filled"
                                                                    sx={{
                                                                        height: 20,
                                                                        minWidth: 24,
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: 600,
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ fontSize: '0.813rem' }}
                                                        >
                                                            {action.description}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        {appIndex < actionsByAppKeys.length - 1 && <Divider sx={{ my: 2.5 }} />}
                    </Box>
                );
            })}
        </Box>
    );
};

export default ApplicationActions;
